"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  ClassForm,
  type Draft,
  EMPTY_DRAFT,
} from "@/components/tools/study/class-form";
import { ClassScheduleGrid } from "@/components/tools/study/class-schedule-grid";
import {
  readStored,
  storageAvailable,
  WeekdayToggles,
  writeStored,
} from "@/components/tools/study/controls";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  CLASS_COLORS,
  type ClassEntry,
  checkClassDraft,
  findOverlaps,
  parseTime,
  prefers24HourClock,
  readSavedSchedule,
  timeFormatter,
  timeValue,
  visibleHours,
} from "@/lib/tool-kit/study/class-schedule";
import { randomToken } from "@/lib/tool-kit/study/dates";

const STORAGE_KEY = "citationeasy-class-schedule-v1";
const HOURS = Array.from({ length: 25 }, (_, hour) => hour);
/** Example week: [days, start, end] per example class, Monday-first days. */
const EXAMPLE_TIMES: readonly [number[], string, string][] = [
  [[0, 2, 4], "09:00", "09:50"],
  [[0, 2, 4], "10:00", "10:50"],
  [[1, 3], "11:00", "12:15"],
  [[3], "13:00", "15:50"],
  [[1, 3], "14:30", "15:45"],
];

/** English strings. A translated content file overrides these through `ui`. */
export const CLASS_SCHEDULE_MAKER_STRINGS = {
  daysLabel: "Days to show",
  weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  weekdaysLong: [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ],
  fromLabel: "Day starts at",
  toLabel: "Day ends at",
  clock24: "24-hour clock",
  midnight: "Midnight",
  needShownDay: "Tick at least one day to show the timetable.",
  addHeading: "Add a class",
  editHeading: "Edit class",
  nameLabel: "Class name",
  namePlaceholder: "e.g. Biology 101",
  classDaysLabel: "Meets on",
  startLabel: "Starts",
  endLabel: "Ends",
  locationLabel: "Location (optional)",
  locationPlaceholder: "e.g. Science Hall 204",
  colorLabel: "Colour",
  colors: [
    "Blue",
    "Orange",
    "Green",
    "Vermilion",
    "Sky blue",
    "Pink",
    "Yellow",
    "Grey",
  ],
  addClass: "Add class",
  saveClass: "Save changes",
  cancelEdit: "Cancel",
  needName: "Give the class a name.",
  needDay: "Choose at least one day the class meets.",
  needTimes: "The class has to end after it starts.",
  classesHeading: "Your classes",
  noClasses:
    "No classes yet. Add one above, or load an example week to see how it works.",
  editLabel: "Edit {name}",
  removeLabel: "Delete {name}",
  edit: "Edit",
  remove: "Delete",
  classSummary: "{days} · {from}–{to}",
  overlap: "{first} and {second} overlap on {day}, {from}–{to}.",
  hiddenClasses:
    "one:{count} class meets on a day that isn't shown. Tick that day to see it.|other:{count} classes meet on days that aren't shown. Tick those days to see them.",
  gridTitle: "Weekly class schedule",
  print: "Print",
  downloadPng: "Download PNG",
  downloadSvg: "Download SVG",
  pngFileName: "class-schedule.png",
  svgFileName: "class-schedule.svg",
  loadExample: "Load an example week",
  exampleNames: [
    "Biology 101",
    "Calculus I",
    "English Composition",
    "Chemistry Lab",
    "Intro to Psychology",
  ],
  exampleLocations: [
    "Science Hall 204",
    "Math 110",
    "Library 3B",
    "Lab B",
    "Room 12",
  ],
  clearAll: "Clear all classes",
  confirmClear: "Click again to clear",
  storageNote:
    "Your timetable is saved in this browser's local storage, so it's still here next time on this device. Nothing is uploaded.",
  storageUnavailable:
    "This browser isn't letting the page save data, so the timetable will clear when you close the page.",
} as const;

export default function ClassScheduleMakerWidget() {
  const strings = useToolUi(CLASS_SCHEDULE_MAKER_STRINGS);
  const locale = useToolLocale();
  const [shownDays, setShownDays] = useState<boolean[]>([
    true,
    true,
    true,
    true,
    true,
    false,
    false,
  ]);
  const [fromHour, setFromHour] = useState("8");
  const [toHour, setToHour] = useState("18");
  const [clock24, setClock24] = useState(() => prefers24HourClock(locale));
  const [classes, setClasses] = useState<ClassEntry[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [clearArmed, setClearArmed] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    setCanSave(storageAvailable());
    const saved = readSavedSchedule(readStored(STORAGE_KEY));
    if (saved) {
      setShownDays(
        [0, 1, 2, 3, 4, 5, 6].map((day) => saved.days.includes(day))
      );
      setFromHour(String(saved.fromHour));
      setToHour(String(saved.toHour));
      setClock24(saved.clock24);
      setClasses(saved.classes);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!(loaded && canSave)) {
      return;
    }
    const timer = setTimeout(() => {
      const ok = writeStored(
        STORAGE_KEY,
        JSON.stringify({
          days: shownDays.flatMap((on, day) => (on ? [day] : [])),
          fromHour: Number(fromHour),
          toHour: Number(toHour),
          clock24,
          classes,
        })
      );
      if (!ok) {
        setCanSave(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [loaded, canSave, shownDays, fromHour, toHour, clock24, classes]);

  const formatTime = useMemo(
    () => timeFormatter(locale, clock24),
    [locale, clock24]
  );
  const days = shownDays.flatMap((on, day) => (on ? [day] : []));
  const overlaps = findOverlaps(classes, days);
  const hidden = classes.filter(
    (entry) => !entry.days.some((day) => shownDays[day])
  ).length;
  const hours = visibleHours(classes, Number(fromHour), Number(toHour));
  const dayList = (list: readonly number[]) =>
    list.map((day) => strings.weekdaysShort[day]).join(", ");

  function submit() {
    const problem = checkClassDraft(draft);
    setError(problem && strings[problem]);
    if (problem) {
      return;
    }
    const entry: ClassEntry = {
      id: draft.id ?? randomToken(),
      name: draft.name.trim().slice(0, 80),
      days: draft.days.flatMap((on, day) => (on ? [day] : [])),
      start: parseTime(draft.start) ?? 0,
      end: parseTime(draft.end) ?? 0,
      location: draft.location.trim().slice(0, 80),
      color: Number(draft.color),
    };
    setClasses((current) =>
      draft.id
        ? current.map((item) => (item.id === draft.id ? entry : item))
        : [...current, entry]
    );
    setDraft({
      ...EMPTY_DRAFT,
      color: String((Number(draft.color) + 1) % CLASS_COLORS.length),
    });
  }

  function edit(entry: ClassEntry) {
    setError(null);
    setDraft({
      id: entry.id,
      name: entry.name,
      days: [0, 1, 2, 3, 4, 5, 6].map((day) => entry.days.includes(day)),
      start: timeValue(entry.start),
      end: timeValue(entry.end),
      location: entry.location,
      color: String(entry.color),
    });
  }

  function loadExample() {
    setClasses(
      EXAMPLE_TIMES.map(([meets, start, end], index) => ({
        id: randomToken(),
        name: strings.exampleNames[index] ?? "",
        location: strings.exampleLocations[index] ?? "",
        days: meets,
        start: parseTime(start) ?? 0,
        end: parseTime(end) ?? 0,
        color: index % CLASS_COLORS.length,
      }))
    );
  }

  function clearAll() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setClasses([]);
    setClearArmed(false);
  }

  const hourOptions = (min: number, max: number) =>
    HOURS.filter((hour) => hour >= min && hour <= max).map((hour) => ({
      value: String(hour),
      label: hour === 24 ? strings.midnight : formatTime(hour * 60),
    }));

  return (
    <>
      <style>
        {"@media print { @page { size: landscape; margin: 10mm; } }"}
      </style>
      <section className="space-y-4 print:hidden">
        <WeekdayToggles
          labels={strings.weekdaysShort}
          legend={strings.daysLabel}
          name="cs-show"
          onChange={setShownDays}
          value={shownDays}
        />
        <div className="grid gap-4 sm:grid-cols-3 sm:items-end">
          <SelectField
            id="cs-from"
            label={strings.fromLabel}
            onChange={setFromHour}
            options={hourOptions(0, 23)}
            value={fromHour}
          />
          <SelectField
            id="cs-to"
            label={strings.toLabel}
            onChange={setToHour}
            options={hourOptions(1, 24)}
            value={toHour}
          />
          <Checkbox
            checked={clock24}
            label={strings.clock24}
            onChange={setClock24}
          />
        </div>
      </section>

      <ClassForm
        draft={draft}
        error={error}
        labels={strings}
        onCancel={() => setDraft(EMPTY_DRAFT)}
        onChange={setDraft}
        onSubmit={submit}
      />

      <section className="space-y-4">
        <h2 className={`${WIDGET_H2} print:hidden`}>{strings.gridTitle}</h2>
        <div aria-live="polite" className="space-y-3 print:hidden">
          {days.length === 0 ? <Notice>{strings.needShownDay}</Notice> : null}
          {overlaps.map((clash) => (
            <ErrorNotice
              key={`${clash.day}-${clash.first.id}-${clash.second.id}`}
            >
              {t(strings.overlap, {
                first: clash.first.name,
                second: clash.second.name,
                day: strings.weekdaysLong[clash.day],
                from: formatTime(clash.start),
                to: formatTime(clash.end),
              })}
            </ErrorNotice>
          ))}
          {hidden > 0 ? (
            <Notice>
              {tPlural(strings.hiddenClasses, locale, hidden, {
                count: hidden,
              })}
            </Notice>
          ) : null}
        </div>
        <ClassScheduleGrid
          classes={classes}
          days={days}
          formatTime={formatTime}
          hours={hours}
          labels={{
            title: strings.gridTitle,
            weekdaysLong: strings.weekdaysLong,
            weekdaysShort: strings.weekdaysShort,
          }}
          svgRef={svgRef}
        />
        <div className="flex flex-wrap gap-3 print:hidden">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              if (svgRef.current) {
                downloadPng(strings.pngFileName, svgRef.current).catch(
                  () => undefined
                );
              }
            }}
            type="button"
          >
            {strings.downloadPng}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              if (svgRef.current) {
                downloadSvg(strings.svgFileName, svgRef.current);
              }
            }}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.loadExample}
          </button>
        </div>
      </section>

      <section className="space-y-3 print:hidden">
        <h2 className={WIDGET_H3}>{strings.classesHeading}</h2>
        {classes.length === 0 ? <Notice>{strings.noClasses}</Notice> : null}
        <ul className="space-y-2">
          {classes.map((entry) => (
            <li
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3"
              key={entry.id}
            >
              <span
                aria-hidden="true"
                className="h-4 w-4 shrink-0 rounded-sm"
                style={{ background: CLASS_COLORS[entry.color]?.edge }}
              />
              <span className="min-w-0 flex-1 text-sm">
                <span className="font-medium text-foreground">
                  {entry.name}
                </span>{" "}
                <span className="text-muted-foreground">
                  {t(strings.classSummary, {
                    days: dayList(entry.days),
                    from: formatTime(entry.start),
                    to: formatTime(entry.end),
                  })}
                  {entry.location ? ` · ${entry.location}` : null}
                </span>
              </span>
              <button
                aria-label={t(strings.editLabel, { name: entry.name })}
                className={BUTTON_SMALL}
                onClick={() => edit(entry)}
                type="button"
              >
                {strings.edit}
              </button>
              <button
                aria-label={t(strings.removeLabel, { name: entry.name })}
                className={BUTTON_SMALL}
                onClick={() =>
                  setClasses(classes.filter((item) => item.id !== entry.id))
                }
                type="button"
              >
                {strings.remove}
              </button>
            </li>
          ))}
        </ul>
        {classes.length > 0 ? (
          <button className={BUTTON_SMALL} onClick={clearAll} type="button">
            {clearArmed ? strings.confirmClear : strings.clearAll}
          </button>
        ) : null}
        <p className={HINT}>
          {canSave ? strings.storageNote : strings.storageUnavailable}
        </p>
      </section>
    </>
  );
}
